'use client';

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { createProcesion, updateProcesion, deleteProcesion, setProcesionStatus, getDiasSS, borrarCecopDia } from '@/lib/actions';
import { ordenarDiasSS } from '@/lib/programa-ss';
import type {
  CecopAsignacion, CecopDia, CecopLive, CecopMapSnapshot, CecopPoint, CecopProcesion, CecopViewport,
  DiaSS, ModCortejoInfo, ModPasoEntity,
} from '@/lib/types';
import { ProcesionMap, isCalibrated } from '@/components/cecop/ProcesionMap';
import { CecopDiaEditor } from '@/components/cecop/CecopDiaEditor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import {
  Loader2, Plus, Radio, Trash2, Save, ArrowLeft, Church, Cross, Route, Crop,
  Play, Square, Copy, MonitorPlay, Users, MapPin, Undo2, Eraser, Radar, Ruler, Globe,
  CalendarDays, ChevronRight,
} from 'lucide-react';

const OBS_BASE = 'https://us-central1-studio-2718359433-2e58f.cloudfunctions.net/cecopOverlay';
const ONLINE_THRESHOLD_MS = 60 * 1000;

type Tool = 'iglesia' | 'estacion' | 'ida' | 'vuelta' | 'viewport' | null;
type PlayerOpt = { uuid: string; name: string; online: boolean };

function toMs(ts: any): number | null {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') return ts.toDate().getTime();
  if (ts.seconds) return ts.seconds * 1000;
  return null;
}

/** La cruz de guía (índice 0) + un puesto por paso, conservando lo ya asignado. */
function buildAsignaciones(numPasos: number, prev: CecopAsignacion[] = []): CecopAsignacion[] {
  const find = (role: 'cruz' | 'paso', index: number) => prev.find((a) => a.role === role && a.index === index);
  const cruz = find('cruz', 0);
  const out: CecopAsignacion[] = [
    { role: 'cruz', index: 0, label: 'Cruz de guía', uuid: cruz?.uuid || '', name: cruz?.name || '' },
  ];
  for (let i = 1; i <= numPasos; i++) {
    const p = find('paso', i);
    out.push({ role: 'paso', index: i, label: p?.label || `Paso ${i}`, uuid: p?.uuid || '', name: p?.name || '' });
  }
  return out;
}

/**
 * Alinea el punto nuevo respecto al anterior para que el tramo salga recto:
 * horizontal, vertical o en diagonal exacta de 45°. Se elige el octante más
 * cercano (tan 67,5° ≈ 2.414 es la frontera entre recto y diagonal).
 */
function snapToAxis(prev: CecopPoint, mc: CecopPoint): CecopPoint {
  const dx = mc.x - prev.x;
  const dz = mc.z - prev.z;
  const ax = Math.abs(dx);
  const az = Math.abs(dz);
  if (ax > az * 2.414) return { x: mc.x, z: prev.z };            // horizontal
  if (az > ax * 2.414) return { x: prev.x, z: mc.z };            // vertical
  const d = Math.round((ax + az) / 2);                            // diagonal 45°
  return { x: prev.x + Math.sign(dx) * d, z: prev.z + Math.sign(dz) * d };
}

const STATUS_STYLE: Record<string, { label: string; cls: string }> = {
  draft: { label: 'Borrador', cls: 'bg-zinc-600/20 text-zinc-300 border-zinc-500/30' },
  live: { label: 'EN DIRECTO', cls: 'bg-rose-500/20 text-rose-300 border-rose-500/40' },
  finished: { label: 'Finalizada', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
};

export default function AdminCecopPage() {
  const { userData, jartinetData, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [tab, setTab] = useState<'procesiones' | 'semanasanta'>('procesiones');
  const [mapCfg, setMapCfg] = useState<CecopMapSnapshot | null>(null);
  const [procesiones, setProcesiones] = useState<CecopProcesion[]>([]);
  const [players, setPlayers] = useState<PlayerOpt[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // ── CECOP de Semana Santa ──
  const [dias, setDias] = useState<DiaSS[]>([]);
  const [cecopDias, setCecopDias] = useState<CecopDia[]>([]);
  // El editor no se monta hasta que han llegado los documentos: se siembra una
  // sola vez con lo guardado (patrón del punto 76) y abrirlo antes de tiempo
  // significaría guardar encima con las asignaciones vacías.
  const [cecopDiasCargados, setCecopDiasCargados] = useState(false);
  const [diaEditando, setDiaEditando] = useState<string | null>(null);
  // Lo que reporta el servidor principal en su latido: entidades de paso y
  // cortejos vivos. Es lo que se asigna a cada puesto (no jugadores).
  const [servidor, setServidor] = useState<{ pasos: ModPasoEntity[]; cortejos: ModCortejoInfo[]; online: boolean }>(
    { pasos: [], cortejos: [], online: false },
  );

  useEffect(() => {
    if (!loading && userData?.role !== 'admin') navigate('/jartinet', { replace: true });
  }, [userData, loading, navigate]);

  // Config del mapa del servidor (se copia dentro de cada procesión al guardar).
  useEffect(() => {
    if (userData?.role !== 'admin') return;
    const u = onSnapshot(doc(db, 'mod_map_config', 'main'), (snap) => {
      const d = snap.exists() ? (snap.data() as any) : {};
      setMapCfg(d.imageUrl ? { imageUrl: d.imageUrl, calib: Array.isArray(d.calib) ? d.calib : [] } : null);
    });
    return () => u();
  }, [userData?.role]);

  // Procesiones.
  useEffect(() => {
    if (userData?.role !== 'admin') return;
    const q = query(collection(db, 'cecop_procesiones'), orderBy('createdAt', 'desc'));
    const u = onSnapshot(q, (snap) => setProcesiones(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))));
    return () => u();
  }, [userData?.role]);

  // Jugadores: los conectados al servidor principal + todos los conocidos.
  useEffect(() => {
    if (userData?.role !== 'admin') return;
    let unsubServer: (() => void) | null = null;
    const online = new Map<string, PlayerOpt>();
    const known = new Map<string, PlayerOpt>();
    const push = () => {
      const merged = new Map<string, PlayerOpt>();
      known.forEach((p, k) => merged.set(k, p));
      online.forEach((p, k) => merged.set(k, { ...p, online: true }));
      setPlayers(
        [...merged.values()].sort((a, b) =>
          a.online === b.online ? a.name.localeCompare(b.name) : a.online ? -1 : 1),
      );
    };

    const uCfg = onSnapshot(doc(db, 'mod_config', 'main'), (snap) => {
      const mainId = snap.exists() ? (snap.data() as any).mainServerId : null;
      unsubServer?.();
      online.clear();
      if (!mainId) { push(); return; }
      unsubServer = onSnapshot(doc(db, 'mod_servers', mainId), (s) => {
        online.clear();
        const d = s.exists() ? (s.data() as any) : {};
        const ms = toMs(d.lastSeen);
        const serverOnline = ms != null && Date.now() - ms < ONLINE_THRESHOLD_MS;
        if (serverOnline && Array.isArray(d.players)) {
          d.players.forEach((p: any) => {
            const key = String(p.uuid || p.name || '');
            if (key) online.set(key, { uuid: p.uuid || '', name: p.name || key, online: true });
          });
        }
        // Pasos y cortejos del servidor (los manda el mismo latido). Se
        // conservan aunque el servidor esté caído: sirven para no perder las
        // asignaciones ya hechas mientras se sigue configurando el día.
        setServidor({
          pasos: Array.isArray(d.pasos) ? (d.pasos as ModPasoEntity[]) : [],
          cortejos: Array.isArray(d.cortejos) ? (d.cortejos as ModCortejoInfo[]) : [],
          online: serverOnline,
        });
        push();
      });
    });

    const uAct = onSnapshot(collection(db, 'mod_player_activity'), (snap) => {
      known.clear();
      snap.docs.forEach((d) => {
        const a = d.data() as any;
        const key = String(a.uuid || a.name || d.id);
        if (key) known.set(key, { uuid: a.uuid || '', name: a.name || key, online: false });
      });
      push();
    });

    return () => { uCfg(); uAct(); unsubServer?.(); };
  }, [userData?.role]);

  // Días del programa (para el CECOP de Semana Santa) y su documento de CECOP.
  useEffect(() => {
    if (userData?.role !== 'admin') return;
    getDiasSS().then((r) => setDias(ordenarDiasSS(r.dias || []))).catch(() => setDias([]));
  }, [userData?.role]);

  useEffect(() => {
    if (userData?.role !== 'admin') return;
    const u = onSnapshot(collection(db, 'cecop_dias'), (snap) => {
      setCecopDias(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
      setCecopDiasCargados(true);
    });
    return () => u();
  }, [userData?.role]);

  const editing = useMemo(() => procesiones.find((p) => p.id === editingId) || null, [procesiones, editingId]);
  const dia = useMemo(() => dias.find((d) => d.id === diaEditando) || null, [dias, diaEditando]);

  const handleCreate = async () => {
    setBusy(true);
    const r = await createProcesion({
      titulo: 'Nueva procesión',
      color: '#d4af37',
      numPasos: 1,
      asignaciones: buildAsignaciones(1),
      map: mapCfg,
    });
    setBusy(false);
    if (r.success && (r as any).id) setEditingId((r as any).id);
    else toast({ title: 'Error', description: r.message, variant: 'destructive' });
  };

  if (loading || userData?.role !== 'admin') {
    return <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-12 w-12 animate-spin text-primary" /></div>;
  }

  if (editing) {
    return (
      <ProcesionEditor
        key={editing.id}
        procesion={editing}
        mapCfg={mapCfg}
        players={players}
        onBack={() => setEditingId(null)}
      />
    );
  }

  if (diaEditando && !cecopDiasCargados) {
    return <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-12 w-12 animate-spin text-primary" /></div>;
  }

  if (dia) {
    return (
      <CecopDiaEditor
        key={dia.id}
        dia={dia}
        cecop={cecopDias.find((c) => c.id === dia.id) || null}
        mapCfg={mapCfg}
        hermandades={jartinetData.hermandades || []}
        pasosServidor={servidor.pasos}
        cortejosServidor={servidor.cortejos}
        servidorOnline={servidor.online}
        onBack={() => setDiaEditando(null)}
      />
    );
  }

  return (
    <div className="space-y-6 pb-12">
      <div className="relative overflow-hidden rounded-3xl border border-white/8 bg-gradient-to-br from-rose-500/15 via-card/40 to-card/20 p-8">
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-rose-500/20 blur-3xl" />
        <div className="relative flex flex-wrap items-center gap-5">
          <div className="grid h-16 w-16 place-items-center rounded-2xl border border-rose-400/30 bg-rose-500/15 text-rose-300">
            <Radar className="h-9 w-9" />
          </div>
          <div className="flex-1 min-w-[16rem]">
            <h1 className="font-headline text-4xl font-bold md:text-5xl">CECOP</h1>
            <p className="mt-2 max-w-2xl text-muted-foreground">
              Centro de control de procesiones. Traza el recorrido, asigna la cruz de guía y cada paso a un jugador,
              y sigue la procesión con GPS en tiempo real.
            </p>
          </div>
          {tab === 'procesiones' && (
            <Button onClick={handleCreate} disabled={busy} className="gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Nueva procesión
            </Button>
          )}
        </div>

        {/* Dos apartados: procesiones sueltas (seguidas por jugadores) y días
            enteros de Semana Santa (seguidos por entidades del servidor). */}
        <div className="relative mt-6 flex flex-wrap gap-2">
          {([
            ['procesiones', 'Procesiones', <Radar key="i" className="h-3.5 w-3.5" />],
            ['semanasanta', 'CECOP Semana Santa', <CalendarDays key="i" className="h-3.5 w-3.5" />],
          ] as const).map(([id, label, icon]) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2 text-sm font-semibold transition-colors',
                tab === id
                  ? 'border-rose-400/40 bg-rose-500/20 text-rose-200'
                  : 'border-white/10 bg-white/[0.03] text-muted-foreground hover:text-foreground',
              )}
            >
              {icon} {label}
            </button>
          ))}
        </div>
      </div>

      {!isCalibrated(mapCfg) && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200">
          El mapa del servidor no está calibrado. Ve a <strong>Mapa Servidor</strong>, sube la imagen y calibra las dos
          esquinas: sin eso no se pueden situar ni el recorrido ni los jugadores.
        </div>
      )}

      {tab === 'semanasanta' ? (
        <DiasSemanaSanta
          dias={dias}
          cecopDias={cecopDias}
          onOpen={(id) => setDiaEditando(id)}
        />
      ) : procesiones.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-white/10 py-20 text-muted-foreground">
          <Radar className="h-10 w-10 opacity-30" />
          <p className="text-sm">Todavía no hay ninguna procesión creada.</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {procesiones.map((p) => (
            <ProcesionCard key={p.id} procesion={p} onEdit={() => setEditingId(p.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── CECOP Semana Santa: lista de días ───────────────────────────────────────

/** Fecha corta del día, tolerante a Timestamp o ISO. */
function fechaCorta(fecha: any): string {
  if (!fecha) return '';
  const d = typeof fecha?.toDate === 'function' ? fecha.toDate() : new Date(fecha);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
}

function DiasSemanaSanta({ dias, cecopDias, onOpen }: {
  dias: DiaSS[];
  cecopDias: CecopDia[];
  onOpen: (id: string) => void;
}) {
  const { toast } = useToast();

  if (dias.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-white/10 py-20 text-muted-foreground">
        <CalendarDays className="h-10 w-10 opacity-30" />
        <p className="text-sm">No hay días en el Programa de Semana Santa.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Elige un día: se abre su mapa con los recorridos de todas las cofradías que salen y ahí se asigna,
        por cofradía, la <strong>entidad de cada paso</strong> y el <strong>cortejo</strong> que hace de cruz de
        guía. Al <strong>ACTIVAR</strong> un día, su panel sale en la portada pública con el GPS en directo.
      </p>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {dias.map((d) => {
          const c = cecopDias.find((x) => x.id === d.id);
          const cofradias = Array.isArray(d.hermandades) ? d.hermandades.length : 0;
          const asignados = (c?.hermandades || []).reduce(
            (n, h) => n + (h.cortejoId != null ? 1 : 0) + h.pasos.filter((p) => p.entityUuid).length, 0);
          const total = (c?.hermandades || []).reduce((n, h) => n + 1 + h.pasos.length, 0);
          return (
            <button
              key={d.id}
              type="button"
              onClick={() => onOpen(d.id)}
              className={cn(
                'group relative overflow-hidden rounded-2xl border bg-card/40 p-5 text-left transition-colors',
                c?.activo ? 'border-rose-500/40' : 'border-white/8 hover:border-white/20',
              )}
            >
              {c?.activo && <div className="absolute inset-y-0 left-0 w-1 bg-rose-500" />}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate font-bold">{c?.titulo || d.nombredia}</h3>
                  <p className="mt-1 text-xs capitalize text-muted-foreground">{fechaCorta(d.fecha) || '—'}</p>
                </div>
                {c?.activo ? (
                  <span className="shrink-0 rounded-full border border-rose-500/40 bg-rose-500/20 px-2 py-0.5 text-[0.6rem] font-bold text-rose-300">
                    <span className="mr-1 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-rose-400" />
                    EN PORTADA
                  </span>
                ) : (
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-[0.65rem] text-muted-foreground">
                <span className="flex items-center gap-1"><Church className="h-3 w-3" /> {cofradias} cofradía{cofradias === 1 ? '' : 's'}</span>
                {c && <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {asignados}/{total} puestos</span>}
                {c?.youtubeUrl && <span className="flex items-center gap-1"><MonitorPlay className="h-3 w-3" /> directo</span>}
                {d.tipo === 'visperas' && <span className="rounded-full bg-white/5 px-1.5">vísperas</span>}
              </div>
              {c && !c.activo && (
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={async (e) => {
                    e.stopPropagation();
                    const r = await borrarCecopDia(d.id);
                    toast({ title: r.success ? 'Eliminado' : 'Error', description: r.message, variant: r.success ? undefined : 'destructive' });
                  }}
                  className="mt-3 inline-flex items-center gap-1 text-[0.65rem] text-destructive/80 hover:text-destructive"
                >
                  <Trash2 className="h-3 w-3" /> Borrar seguimiento
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Tarjeta de la lista ─────────────────────────────────────────────────────

function ProcesionCard({ procesion, onEdit }: { procesion: CecopProcesion; onEdit: () => void }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const st = STATUS_STYLE[procesion.status] || STATUS_STYLE.draft;
  const asignados = (procesion.asignaciones || []).filter((a) => a.uuid || a.name).length;
  const total = (procesion.numPasos || 0) + 1;

  const toggle = async () => {
    setBusy(true);
    const r = await setProcesionStatus(procesion.id, procesion.status === 'live' ? 'finished' : 'live');
    setBusy(false);
    toast({ title: r.success ? 'Listo' : 'Error', description: r.message, variant: r.success ? undefined : 'destructive' });
  };

  const remove = async () => {
    const r = await deleteProcesion(procesion.id);
    toast({ title: r.success ? 'Eliminada' : 'Error', description: r.message, variant: r.success ? undefined : 'destructive' });
  };

  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/8 bg-card/40 p-5">
      <div className="absolute inset-y-0 left-0 w-1" style={{ background: procesion.color || '#d4af37' }} />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate font-bold">{procesion.titulo}</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {procesion.numPasos} paso{procesion.numPasos === 1 ? '' : 's'} · {asignados}/{total} asignados
          </p>
        </div>
        <span className={cn('shrink-0 rounded-full border px-2 py-0.5 text-[0.6rem] font-bold', st.cls)}>
          {procesion.status === 'live' && <span className="mr-1 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-rose-400" />}
          {st.label}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap gap-2 text-[0.65rem] text-muted-foreground">
        <span className="flex items-center gap-1"><Route className="h-3 w-3" /> {(procesion.ida || []).length} ida</span>
        <span className="flex items-center gap-1"><Route className="h-3 w-3" /> {(procesion.vuelta || []).length} vuelta</span>
        {procesion.viewport && <span className="flex items-center gap-1"><Crop className="h-3 w-3" /> recorte</span>}
        {procesion.publicCard && (
          <span className="flex items-center gap-1 rounded-full bg-emerald-500/15 px-1.5 text-emerald-300">
            <Globe className="h-3 w-3" /> en portada
          </span>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button size="sm" variant="secondary" onClick={onEdit} className="gap-1.5">
          <MapPin className="h-3.5 w-3.5" /> Editar
        </Button>
        <Button
          size="sm"
          onClick={toggle}
          disabled={busy}
          variant={procesion.status === 'live' ? 'destructive' : 'default'}
          className="gap-1.5"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : procesion.status === 'live' ? <Square className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          {procesion.status === 'live' ? 'Finalizar' : 'Iniciar'}
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="ghost" className="gap-1.5 text-destructive hover:text-destructive">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar «{procesion.titulo}»?</AlertDialogTitle>
              <AlertDialogDescription>
                Se borra la procesión y su seguimiento. Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground">Eliminar</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

// ─── Editor ──────────────────────────────────────────────────────────────────

function ProcesionEditor({
  procesion, mapCfg, players, onBack,
}: {
  procesion: CecopProcesion;
  mapCfg: CecopMapSnapshot | null;
  players: PlayerOpt[];
  onBack: () => void;
}) {
  const { toast } = useToast();

  const [titulo, setTitulo] = useState(procesion.titulo || '');
  const [descripcion, setDescripcion] = useState(procesion.descripcion || '');
  const [color, setColor] = useState(procesion.color || '#d4af37');
  const [publicCard, setPublicCard] = useState(!!procesion.publicCard);
  const [youtubeUrl, setYoutubeUrl] = useState(procesion.youtubeUrl || '');
  const [numPasos, setNumPasos] = useState(procesion.numPasos || 1);
  const [asignaciones, setAsignaciones] = useState<CecopAsignacion[]>(
    buildAsignaciones(procesion.numPasos || 1, procesion.asignaciones || []),
  );
  const [iglesia, setIglesia] = useState<CecopPoint | null>(procesion.iglesia ?? null);
  const [estacion, setEstacion] = useState<CecopPoint | null>(procesion.estacion ?? null);
  const [ida, setIda] = useState<CecopPoint[]>(procesion.ida || []);
  const [vuelta, setVuelta] = useState<CecopPoint[]>(procesion.vuelta || []);
  const [viewport, setViewport] = useState<CecopViewport | null>(procesion.viewport ?? null);
  const [tool, setTool] = useState<Tool>(null);
  const [snap, setSnap] = useState(true);
  const [previewCrop, setPreviewCrop] = useState(false);
  const [live, setLive] = useState<CecopLive | null>(null);
  const [saving, setSaving] = useState(false);

  // Seguimiento en vivo (para ver el GPS mientras se edita, si está en directo).
  useEffect(() => {
    const u = onSnapshot(doc(db, 'cecop_live', procesion.id), (snap) =>
      setLive(snap.exists() ? ({ id: snap.id, ...(snap.data() as any) }) : null));
    return () => u();
  }, [procesion.id]);

  // El mapa efectivo es el del servidor; si aún no ha cargado se usa el guardado.
  const map = mapCfg || procesion.map || null;

  const setPasos = (n: number) => {
    const v = Math.max(0, Math.min(20, Math.floor(n) || 0));
    setNumPasos(v);
    setAsignaciones((prev) => buildAsignaciones(v, prev));
  };

  const setAsig = (index: number, role: 'cruz' | 'paso', patch: Partial<CecopAsignacion>) => {
    setAsignaciones((prev) => prev.map((a) => (a.role === role && a.index === index ? { ...a, ...patch } : a)));
  };

  const handleMapClick = ({ mc }: { mc: CecopPoint | null }) => {
    if (!mc || !tool) return;
    if (tool === 'iglesia') { setIglesia(mc); setTool(null); }
    else if (tool === 'estacion') { setEstacion(mc); setTool(null); }
    // Con "Recto" activo el punto se alinea con el anterior (H, V o 45°).
    else if (tool === 'ida') setIda((p) => [...p, snap && p.length ? snapToAxis(p[p.length - 1], mc) : mc]);
    else if (tool === 'vuelta') setVuelta((p) => [...p, snap && p.length ? snapToAxis(p[p.length - 1], mc) : mc]);
  };

  const removePoint = (kind: 'ida' | 'vuelta', index: number) => {
    if (kind === 'ida') setIda((p) => p.filter((_, i) => i !== index));
    else setVuelta((p) => p.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    const r = await updateProcesion(procesion.id, {
      titulo: titulo.trim() || 'Procesión sin título',
      descripcion,
      color,
      publicCard,
      youtubeUrl: youtubeUrl.trim(),
      numPasos,
      asignaciones,
      iglesia,
      estacion,
      ida,
      vuelta,
      viewport,
      map,
    });
    setSaving(false);
    toast({ title: r.success ? 'Guardado' : 'Error', description: r.message, variant: r.success ? undefined : 'destructive' });
  };

  const obsUrl = `${OBS_BASE}?id=${procesion.id}&token=${procesion.obsToken || ''}`;
  const copyObs = async () => {
    try {
      await navigator.clipboard.writeText(obsUrl);
      toast({ title: 'Copiado', description: 'URL de la fuente de OBS copiada al portapapeles.' });
    } catch {
      toast({ title: 'No se pudo copiar', description: obsUrl, variant: 'destructive' });
    }
  };

  const toolBtn = (t: Tool, icon: React.ReactNode, label: string) => (
    <Button
      size="sm"
      variant={tool === t ? 'default' : 'secondary'}
      onClick={() => { setTool(tool === t ? null : t); if (t === 'viewport') setPreviewCrop(false); }}
      className="gap-1.5"
    >
      {icon} {label}
    </Button>
  );

  return (
    <div className="space-y-5 pb-12">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5"><ArrowLeft className="h-4 w-4" /> Volver</Button>
        <h1 className="flex-1 truncate text-2xl font-bold">{titulo || 'Procesión'}</h1>
        {procesion.status === 'live' && (
          <span className="flex items-center gap-1.5 rounded-full border border-rose-500/40 bg-rose-500/20 px-3 py-1 text-xs font-bold text-rose-300">
            <Radio className="h-3.5 w-3.5 animate-pulse" /> EN DIRECTO
          </span>
        )}
        <Button onClick={handleSave} disabled={saving} className="gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar
        </Button>
      </div>

      <div className="grid gap-5 xl:grid-cols-[1fr_380px]">
        {/* Mapa + herramientas */}
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-white/8 bg-card/40 p-3">
            {toolBtn('iglesia', <Church className="h-3.5 w-3.5" />, 'Iglesia')}
            {toolBtn('estacion', <Cross className="h-3.5 w-3.5" />, 'Estación')}
            {toolBtn('ida', <Route className="h-3.5 w-3.5" />, 'Ida')}
            {toolBtn('vuelta', <Route className="h-3.5 w-3.5" />, 'Vuelta')}
            {toolBtn('viewport', <Crop className="h-3.5 w-3.5" />, 'Recorte GPS')}

            <span className="mx-1 h-5 w-px bg-white/10" />
            <Button
              size="sm"
              variant={snap ? 'default' : 'secondary'}
              onClick={() => setSnap((v) => !v)}
              title="Alinea cada punto con el anterior en horizontal, vertical o 45° para que el tramo salga perfectamente recto."
              className="gap-1.5"
            >
              <Ruler className="h-3.5 w-3.5" /> Recto {snap ? 'ON' : 'OFF'}
            </Button>

            <div className="ml-auto flex items-center gap-2">
              {viewport && (
                <Button size="sm" variant={previewCrop ? 'default' : 'outline'} onClick={() => { setPreviewCrop((v) => !v); setTool(null); }} className="gap-1.5">
                  <Crop className="h-3.5 w-3.5" /> {previewCrop ? 'Ver mapa completo' : 'Ver recorte'}
                </Button>
              )}
              {tool === 'ida' && ida.length > 0 && (
                <Button size="sm" variant="ghost" onClick={() => setIda((p) => p.slice(0, -1))} className="gap-1.5"><Undo2 className="h-3.5 w-3.5" /> Deshacer</Button>
              )}
              {tool === 'vuelta' && vuelta.length > 0 && (
                <Button size="sm" variant="ghost" onClick={() => setVuelta((p) => p.slice(0, -1))} className="gap-1.5"><Undo2 className="h-3.5 w-3.5" /> Deshacer</Button>
              )}
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            {tool === 'viewport'
              ? 'Arrastra sobre el mapa para elegir el trozo que se verá en el GPS y en OBS.'
              : tool === 'ida' || tool === 'vuelta'
                ? snap
                  ? 'Clic para añadir puntos: cada uno se alinea con el anterior (horizontal, vertical o 45°) para que la línea salga recta. Clic en un punto lo borra.'
                  : 'Clic para añadir puntos libres. Activa «Recto» para alinearlos. Clic en un punto lo borra.'
                : tool
                  ? 'Haz clic en el mapa para colocar el punto.'
                  : 'Elige una herramienta para trazar el recorrido.'}
          </p>

          <ProcesionMap
            map={map}
            viewport={previewCrop ? viewport : null}
            color={color}
            iglesia={iglesia}
            estacion={estacion}
            ida={ida}
            vuelta={vuelta}
            live={live?.entries || []}
            showLabels
            onMapClick={tool && tool !== 'viewport' ? handleMapClick : undefined}
            dragSelect={tool === 'viewport'}
            onDragSelect={(rect) => { setViewport(rect); setTool(null); setPreviewCrop(true); }}
            showPointHandles={!!tool && tool !== 'viewport'}
            onPointClick={removePoint}
          />

          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>Ida: <strong className="text-foreground">{ida.length}</strong> puntos</span>
            <span>Vuelta: <strong className="text-foreground">{vuelta.length}</strong> puntos</span>
            <Button size="sm" variant="ghost" onClick={() => setIda([])} className="h-7 gap-1 text-xs"><Eraser className="h-3 w-3" /> Limpiar ida</Button>
            <Button size="sm" variant="ghost" onClick={() => setVuelta([])} className="h-7 gap-1 text-xs"><Eraser className="h-3 w-3" /> Limpiar vuelta</Button>
            {viewport && <Button size="sm" variant="ghost" onClick={() => { setViewport(null); setPreviewCrop(false); }} className="h-7 gap-1 text-xs"><Eraser className="h-3 w-3" /> Quitar recorte</Button>}
          </div>
        </div>

        {/* Panel de configuración */}
        <div className="space-y-4">
          <div className="space-y-3 rounded-2xl border border-white/8 bg-card/40 p-4">
            <h3 className="font-semibold">Datos</h3>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Título</Label>
              <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Ej. Hermandad de la Vera Cruz" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Descripción</Label>
              <Textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={2} placeholder="Opcional" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Color</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={/^#[0-9a-fA-F]{6}$/.test(color) ? color : '#d4af37'}
                    onChange={(e) => setColor(e.target.value)}
                    className="h-10 w-12 shrink-0 cursor-pointer rounded-md border border-white/10 bg-transparent"
                  />
                  <Input value={color} onChange={(e) => setColor(e.target.value)} placeholder="#d4af37" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Nº de pasos</Label>
                <Input type="number" min={0} max={20} value={numPasos} onChange={(e) => setPasos(Number(e.target.value))} />
              </div>
            </div>
          </div>

          {/* Asignación de jugadores */}
          <div className="space-y-3 rounded-2xl border border-white/8 bg-card/40 p-4">
            <h3 className="flex items-center gap-2 font-semibold">
              <Users className="h-4 w-4 text-primary" /> Puestos
              <span className="ml-auto text-xs font-normal text-muted-foreground">{players.filter((p) => p.online).length} en línea</span>
            </h3>
            <p className="text-xs text-muted-foreground">
              Cada puesto se sigue por el jugador asignado. Su posición se actualiza con el latido del servidor (~15 s).
            </p>
            {asignaciones.map((a) => {
              const key = `${a.role}-${a.index}`;
              const selected = a.uuid || a.name;
              const entry = live?.entries?.find((e) => e.role === a.role && e.index === a.index);
              return (
                <div key={key} className="space-y-1.5 rounded-xl border border-white/8 bg-muted/20 p-3">
                  <div className="flex items-center gap-2">
                    <span
                      className="grid h-5 w-5 shrink-0 place-items-center rounded-full text-[0.55rem] font-black text-black"
                      style={{ background: a.role === 'cruz' ? '#ffffff' : color }}
                    >
                      {a.role === 'cruz' ? '✚' : a.index}
                    </span>
                    {a.role === 'cruz' ? (
                      <span className="text-sm font-semibold">Cruz de guía</span>
                    ) : (
                      <Input
                        value={a.label}
                        onChange={(e) => setAsig(a.index, a.role, { label: e.target.value })}
                        className="h-7 text-sm"
                        placeholder={`Paso ${a.index}`}
                      />
                    )}
                    {entry && (
                      <span className={cn('ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[0.55rem] font-bold',
                        entry.online ? 'bg-emerald-500/20 text-emerald-300' : 'bg-zinc-600/20 text-zinc-400')}>
                        {entry.online ? 'GPS' : 'offline'}
                      </span>
                    )}
                  </div>
                  <select
                    value={selected}
                    onChange={(e) => {
                      const p = players.find((x) => (x.uuid || x.name) === e.target.value);
                      setAsig(a.index, a.role, { uuid: p?.uuid || '', name: p?.name || '' });
                    }}
                    className="w-full rounded-md border border-white/10 bg-background px-2 py-1.5 text-sm"
                  >
                    <option value="">— Sin asignar —</option>
                    {players.map((p) => (
                      <option key={p.uuid || p.name} value={p.uuid || p.name}>
                        {p.online ? '● ' : '○ '}{p.name}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>

          {/* Tarjeta pública de la home */}
          <div className="space-y-3 rounded-2xl border border-white/8 bg-card/40 p-4">
            <h3 className="flex items-center gap-2 font-semibold"><Globe className="h-4 w-4 text-emerald-400" /> Tarjeta pública</h3>
            <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-white/8 bg-muted/20 p-3">
              <input
                type="checkbox"
                checked={publicCard}
                onChange={(e) => setPublicCard(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-emerald-500"
              />
              <span className="text-sm">
                Habilitar tarjeta pública
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  Aparece en la portada de la web (bajo el lema del servidor) con el directo y el GPS.
                  Mientras esté activa se ocultan el emblema «11 Jartiland» y los botones de entrar/Discord.
                </span>
              </span>
            </label>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Directo de YouTube</Label>
              <Input
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
              />
              <p className="text-[0.65rem] text-muted-foreground">
                Vale cualquier formato (watch, youtu.be, /live/). Se incrusta silenciado: los navegadores
                bloquean la reproducción automática con sonido.
              </p>
            </div>
            {publicCard && (
              <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-2 text-[0.65rem] text-amber-200">
                Solo debería haber una tarjeta pública activa a la vez. Si hay varias, la portada muestra una cualquiera.
              </p>
            )}
          </div>

          {/* Fuente de OBS */}
          <div className="space-y-2 rounded-2xl border border-white/8 bg-card/40 p-4">
            <h3 className="flex items-center gap-2 font-semibold"><MonitorPlay className="h-4 w-4 text-sky-400" /> Fuente para OBS</h3>
            <p className="text-xs text-muted-foreground">
              Añade una <strong>Fuente de navegador</strong> en OBS con esta URL (fondo transparente, 1280×720 recomendado).
              La URL es pública: quien la tenga ve el GPS, así que no la compartas fuera de la retransmisión.
            </p>
            <div className="flex gap-2">
              <Input readOnly value={obsUrl} className="text-[0.65rem]" onFocus={(e) => e.currentTarget.select()} />
              <Button size="icon" variant="secondary" onClick={copyObs}><Copy className="h-4 w-4" /></Button>
            </div>
            <Button size="sm" variant="outline" asChild className="w-full gap-1.5">
              <a href={obsUrl} target="_blank" rel="noreferrer"><MonitorPlay className="h-3.5 w-3.5" /> Previsualizar</a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
